import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_cr = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import { isPasswordProperty } from "../state/password";
import { UpdateMutationBuilder, InitializeMutationBuilder } from "./MutationBuilder";
import * as Entry from "../state/Entry";
import { iReference } from "../state/Reference";
import { isResolved } from "../Status";
import { setEntryLocationToRemote } from "../state/setLocation";
import resetModifiedState from "../state/resetModifiedState";
import { client_state } from "../state/ClientState";
import { setToSaving } from "../state/SaveModifiedStatus";
import { iCollection } from "../state/Collection";
import { updatePermissions } from "../state 2 interface/permissions";

export type MutationObject = {
	mutation: api_cr.Tmutation;
	onSucces: () => void;
	onFailed: (permission_denied: boolean) => void;
};

function createInitializeNode(node: api_cs.Cnode, builder: InitializeMutationBuilder): api_cr.Tnode_initialization {
	for (const [attribute_name, attribute] of node.input.node_type().properties.attributes) {
		switch (attribute.properties.type.state.name) {
			case "property": {
				const property_type = attribute.properties.type.state.node;
				const value_source = property_type.properties.type.state.node.output.value_source();
				switch (value_source.variant.name) {
					case "base": {
						const base_type = value_source.variant.definition;
						switch (base_type.variant.name) {
							case "external": {
								const $property = node.properties.attributes.get(attribute_name)?.properties.type.cast("property");
								if ($property === undefined) {
									throw new Error(`Missing property '${attribute_name}' in client state. In: ${node.path}`);
								}
								switch (property_type.properties.type.state.name) {
									case "collection": {
										builder.collection(attribute_name);
										const collection = $property.properties.type.cast("collection");
										for (const entry of collection.properties.entries) {
											createInitializeNode(
												entry[1].properties.node,
												builder.collectionEntry(
													attribute_name,
													entry[1].properties.key.ref.properties.is_set.switch({
														yes: ($) => $.properties.text,
														no: () => "",
													})
												)
											);
											entry[1].properties.node.properties.is_busy_saving.ensure("yes", {});
										}
										break;
									}
									case "file": {
										const file = $property.properties.type.cast("file");
										switch (file.properties.is_set.state.name) {
											case "no":
												throw new Error(`file ${attribute_name} is not set. In: ${node.path}`);
											case "yes": {
												const is_set = file.properties.is_set.state.node;
												switch (file.properties.is_editable.state.name) {
													case "no":
														break;
													case "yes": {
														const yes_is_editable = file.properties.is_editable.state.node;
														switch (yes_is_editable.properties.modified.state.name) {
															case "no":
																break;
															case "yes": {
																const yes_modified = yes_is_editable.properties.modified.state.node;
																setToSaving(yes_modified);
																break;
															}
														}
														break;
													}
												}
												builder.file(attribute_name, is_set.properties.token, is_set.properties.extension);
												break;
											}
										}
										break;
									}
									case "group":
										createInitializeNode(
											$property.properties.type.cast("group").properties.node,
											builder.group(attribute_name)
										);
										break;
									case "number": {
										const $number = $property.properties.type.cast("number");
										const value = $number.properties.is_valid.switch({
											no: function (): number {
												throw new Error(`number ${attribute_name} is not valid. In: ${node.path}`);
											},
											yes: function () {
												return $number.properties.is_set.switch({
													yes: function ($) {
														return $.properties.number.value;
													},
													no: function (): number {
														throw new Error(`number ${attribute_name} is not set. In: ${node.path}`);
													},
												});
											},
										});
										switch ($number.properties.is_editable.state.name) {
											case "no":
												break;
											case "yes": {
												const yes_is_editable = $number.properties.is_editable.state.node;
												switch (yes_is_editable.properties.modified.state.name) {
													case "no":
														break;
													case "yes": {
														const yes_modified = yes_is_editable.properties.modified.state.node;
														setToSaving(yes_modified);
														break;
													}
												}
												break;
											}
										}
										builder.number(attribute_name, value);
										break;
									}
									case "state group": {
										const state_group_property = $property.properties.type.cast("state group");
										switch (state_group_property.properties.is_modified.state.name) {
											case "no":
												break;
											case "yes": {
												const yes_is_modified = state_group_property.properties.is_modified.state.node;
												setToSaving(yes_is_modified);
												break;
											}
										}
										switch (state_group_property.properties.is_set.state.name) {
											case "no":
												throw new Error(`state group ${attribute_name} is not set. In: ${node.path}`);
											case "yes": {
												const $ = state_group_property.properties.is_set.state.node;
												createInitializeNode(
													$.properties.state.ref.properties.node,
													builder.state(attribute_name, $.properties.state.entry)
												);
												break;
											}
										}
										break;
									}
									case "text": {
										const $text = property_type.properties.type.state.node;
										const text_property = $property.properties.type.cast("text");
										let text_value = text_property.properties.is_set.switch({
											no: () => "",
											yes: (is_set) => is_set.properties.text,
										});
										if (isPasswordProperty($text)) {
											// password properties must be set by the client protocol.
											text_value = "";
										}
										switch (text_property.properties.is_valid.state.name) {
											case "no":
												throw new Error(`text ${attribute_name} is not valid. In: ${node.path}`);
											case "yes":
												builder.text(attribute_name, text_value);
												break;
										}
										switch (text_property.properties.is_editable.state.name) {
											case "no":
												break;
											case "yes": {
												const yes_is_editable = text_property.properties.is_editable.state.node;
												switch (yes_is_editable.properties.modified.state.name) {
													case "no":
														break;
													case "yes": {
														const yes_modified = yes_is_editable.properties.modified.state.node;
														setToSaving(yes_modified);
														break;
													}
												}
												break;
											}
										}
										break;
									}
								}
								break;
							}
							case "internal":
								break;
						}
						break;
					}
					case "derived":
						break;
				}
				break;
			}
			case "action":
				break;
			case "command":
				break;
			case "reference set":
				break;
		}
	}
	return builder.node;
}

function createUpdateNode(node: api_cs.Cnode, builder: UpdateMutationBuilder): UpdateMutationBuilder {
	for (const [name, attribute] of node.properties.attributes) {
		switch (attribute.properties.type.state.name) {
			case "operation":
				break;
			case "property": {
				const property_type = attribute.properties.type.state.node.properties.type.state;
				switch (property_type.name) {
					case "text": {
						const text = property_type.node;
						switch (text.properties.is_valid.state.name) {
							case "no":
								break;
							case "yes":
								switch (text.properties.is_editable.state.name) {
									case "no":
										break;
									case "yes": {
										const modified = text.properties.is_editable.state.node.properties.modified.state;
										switch (modified.name) {
											case "no":
												break;
											case "yes": {
												const is_set = text.properties.is_set.state;
												switch (is_set.name) {
													case "no":
														break;
													case "yes":
														// password properties must be set by the client protocol.
														if (!isPasswordProperty(text.inferences.text_type())) {
															setToSaving(modified.node);
															modified.node.properties.saving.ensure("yes", {});
															builder.text(name, is_set.node.properties.text);
														}
														break;
												}
												break;
											}
										}
										break;
									}
								}
								break;
						}
						break;
					}
					case "number": {
						const number = property_type.node;
						const number_type = number.inferences.number_type().properties.type.state;
						switch (number_type.name) {
							case "base": {
								switch (number_type.node.properties.type.state.name) {
									case "simple":
										switch (number.properties.is_valid.state.name) {
											case "no":
												break;
											case "yes":
												switch (number.properties.is_editable.state.name) {
													case "no":
														break;
													case "yes": {
														const modified = number.properties.is_editable.state.node.properties.modified.state;
														switch (modified.name) {
															case "no":
																break;
															case "yes": {
																setToSaving(modified.node);
																builder.number(name, modified.node.properties.number.value);
																break;
															}
														}
														break;
													}
												}
												break;
										}
										break;
									case "causal":
								}
								break;
							}
							case "derived":
						}
						break;
					}
					case "file": {
						const file = property_type.node;
						switch (file.properties.is_valid.state.name) {
							case "no":
								break;
							case "yes":
								switch (file.properties.is_editable.state.name) {
									case "no":
										break;
									case "yes": {
										const modified = file.properties.is_editable.state.node.properties.modified.state;
										switch (modified.name) {
											case "no":
												break;
											case "yes": {
												switch (modified.node.properties.upload_status.state.name) {
													case "not uploaded":
														break;
													case "uploading":
														break;
													case "done": {
														const done = modified.node.properties.upload_status.state.node;
														setToSaving(modified.node);
														builder.file(name, done.properties.token, done.parent.properties.extension);
														break;
													}
												}
												break;
											}
										}
										break;
									}
								}
								break;
						}
						break;
					}
					case "group": {
						const node = property_type.node.properties.node;
						switch (node.properties.modified.state.name) {
							case "yes":
								createUpdateNode(node, builder.group(name));
								break;
							case "no":
						}
						break;
					}
					case "state group": {
						const stategroup = property_type.node;
						const is_set = stategroup.properties.is_set.state;
						switch (stategroup.properties.is_modified.state.name) {
							case "no":
								break;
							case "yes": {
								const yes_is_modified = stategroup.properties.is_modified.state.node;
								setToSaving(yes_is_modified);
								break;
							}
						}
						switch (is_set.name) {
							case "yes": {
								const state = is_set.node.properties.state.ref;
								switch (state.properties.location.state.name) {
									case "remote":
										switch (state.properties.node.properties.modified.state.name) {
											case "yes":
												createUpdateNode(state.properties.node, builder.update_state(name, state.key_value));
												break;
											case "no":
										}
										break;
									case "local":
										switch (stategroup.properties.is_editable.state.name) {
											case "yes":
												createInitializeNode(state.properties.node, builder.set_state(name, state.key_value));
												break;
											case "no":
										}
										break;
								}
								break;
							}
							case "no":
						}
						break;
					}
					case "collection":
						break;
				}
				break;
			}
		}
	}
	return builder;
}

export function forNode(node: api_cs.Cnode, save_all: boolean): MutationObject {
	const mutation = createMutation(findEntriesToInclude(node, save_all));
	createUpdateNode(
		node,
		createMutationContextForNode(node, new UpdateMutationBuilder(mutation.mutation["node update"]))
	);
	return mutation;
}

export function forCollection(collection: api_cs.Ccollection, save_all: boolean): MutationObject {
	return createMutation(findEntriesToIncludeForCollection(collection, save_all));
}

export function forEntry(entry: api_cs.Centries__collection, save_all: boolean): MutationObject {
	return createMutation(findEntriesToIncludeFromEntry(entry, save_all));
}

export function createMutation(entries: Set<api_cs.Centries__collection>) {
	const root_mutation = UpdateMutationBuilder.create();
	const entry_mutations = new Map<api_cs.Centries__collection, "add" | "update" | "delete">();
	for (const entry of entries) {
		const collection_name = entry.parent.parent.parent.key.entry;
		const mutation = createMutationContextForNode(entry.component_root, root_mutation);
		switch (entry.properties.location.state.name) {
			case "remote": {
				const remote_entry = entry.properties.location.state.node;
				const key = Entry.getKeyBackendValue(entry) ?? "";
				switch (remote_entry.properties.marked_for_deletion.state.name) {
					case "no":
						switch (entry.properties.node.properties.modified.state.name) {
							case "no":
								break;
							case "yes":
								entry_mutations.set(entry, "update");
								createUpdateNode(entry.properties.node, mutation.updateCollectionEntry(collection_name, key));
								break;
						}
						break;
					case "yes":
						entry_mutations.set(entry, "delete");
						mutation.deleteCollectionEntry(collection_name, key);
						break;
				}
				break;
			}
			case "local": {
				entry_mutations.set(entry, "add");
				entry.properties.node.properties.is_busy_saving.set("yes", {});
				const key = Entry.getKeyValue(entry) ?? "";
				createInitializeNode(entry.properties.node, mutation.addCollectionEntry(collection_name, key));
				break;
			}
		}
	}
	return {
		mutation: {
			"context node": new IdPathBuilder().build(),
			"node update": root_mutation.node,
		},
		onSucces: () => {
			client_state.root.transact(() => {
				for (const [entry, operation] of entry_mutations) {
					if (entry.destroyed) continue;
					switch (operation) {
						case "add":
						case "update":
							updatePermissions(entry.properties.node);
							setEntryLocationToRemote(entry);
							resetModifiedState(entry.properties.node, true);
							entry.properties.node.properties.is_busy_saving.set("no", {});
							break;
						case "delete":
							iCollection(entry.parent).deleteServerEntry(entry);
							break;
					}
				}
			});
		},
		onFailed: (permission_denied: boolean) => {
			for (const [entry, operation] of entry_mutations) {
				if (entry.destroyed) continue;
				if (permission_denied) {
					updatePermissions(entry.properties.node);
				}
				entry.properties.node.properties.is_busy_saving.set("no", {});
				switch (operation) {
					case "add":
						break;
					case "update":
						break;
					case "delete":
						entry.properties.location.cast("remote").properties.marked_for_deletion.ensure("no", {});
						break;
				}
			}
		},
	};
}

function findEntriesToInclude(node: api_cs.Cnode, save_all: boolean) {
	return _findEntriesToInclude(node, save_all, new Set(), new Set());
}
function _findEntriesToInclude(
	node: api_cs.Cnode,
	save_all: boolean,
	entries: Set<api_cs.Centries__collection>,
	visited_entries: Set<api_cs.Centries__collection>
) {
	for (const attribute of node.properties.attributes.values()) {
		switch (attribute.properties.type.state.name) {
			case "property": {
				const property = attribute.properties.type.state.node;
				const value_source = property.inferences.property_type().properties.type.state.node.output.value_source();
				switch (value_source.variant.name) {
					case "base": {
						const base_type = value_source.variant.definition;
						switch (base_type.variant.name) {
							case "internal":
								break;
							case "external":
								switch (property.properties.type.state.name) {
									case "text": {
										const navigable = property.properties.type.state.node.properties.navigable.state;
										switch (navigable.name) {
											case "no":
												break;
											case "yes": {
												const referenced_node = iReference(navigable.node).referencedNode.value;
												if (isResolved(referenced_node)) {
													switch (referenced_node.properties.location.state.name) {
														case "remote":
															break;
														case "local":
															switch (referenced_node.properties.is_valid.state.name) {
																case "yes":
																	_findEntriesToIncludeFromEntry(
																		referenced_node.input.entity().cast("entry"),
																		save_all,
																		entries,
																		visited_entries
																	);
																	break;
																case "no":
															}
															break;
													}
												}
												break;
											}
										}
										break;
									}
									case "number":
										break;
									case "file":
										break;
									case "group":
										_findEntriesToInclude(
											property.properties.type.state.node.properties.node,
											save_all,
											entries,
											visited_entries
										);
										break;
									case "state group": {
										const is_set = property.properties.type.state.node.properties.is_set.state;
										switch (is_set.name) {
											case "no":
												break;
											case "yes": {
												const state = is_set.node.properties.state.ref;
												_findEntriesToInclude(state.properties.node, save_all, entries, visited_entries);
												break;
											}
										}
										break;
									}
									case "collection": {
										const collection = property.properties.type.state.node;
										switch (collection.inferences.collection_type().properties.ui.properties.size.state.name) {
											case "large":
												switch (node.properties.location.state.name) {
													case "local":
														_findEntriesToIncludeForCollection(collection, save_all, entries, visited_entries);
														break;
													case "remote":
														if (save_all) {
															_findEntriesToIncludeForCollection(collection, save_all, entries, visited_entries);
														}
												}
												break;
											case "small":
												_findEntriesToIncludeForCollection(collection, save_all, entries, visited_entries);
												break;
										}
										break;
									}
								}
								break;
						}
						break;
					}
					case "derived":
						break;
				}
				break;
			}
			case "operation":
		}
	}
	return entries;
}

function findEntriesToIncludeForCollection(collection: api_cs.Ccollection, save_all: boolean) {
	return _findEntriesToIncludeForCollection(collection, save_all, new Set(), new Set());
}
function _findEntriesToIncludeForCollection(
	collection: api_cs.Ccollection,
	save_all: boolean,
	entries: Set<api_cs.Centries__collection>,
	visited_entries: Set<api_cs.Centries__collection>
) {
	for (const entry of collection.properties.entries.values()) {
		_findEntriesToIncludeFromEntry(entry, save_all, entries, visited_entries);
	}
	return entries;
}

function findEntriesToIncludeFromEntry(entry: api_cs.Centries__collection, save_all: boolean) {
	return _findEntriesToIncludeFromEntry(entry, save_all, new Set(), new Set());
}
function _findEntriesToIncludeFromEntry(
	entry: api_cs.Centries__collection,
	save_all: boolean,
	entries: Set<api_cs.Centries__collection>,
	visited_entries: Set<api_cs.Centries__collection>
) {
	if (visited_entries.has(entry)) return entries;
	visited_entries.add(entry);
	switch (entry.component_root.properties.location.state.name) {
		case "remote":
			switch (entry.properties.location.state.name) {
				case "remote":
					switch (entry.properties.location.state.node.properties.marked_for_deletion.state.name) {
						case "no":
							switch (entry.properties.node.properties.modified.state.name) {
								case "no":
									if (save_all) {
										entries.add(entry);
										_findEntriesToInclude(entry.properties.node, save_all, entries, visited_entries);
									}
									break;
								case "yes":
									entries.add(entry);
									_findEntriesToInclude(entry.properties.node, save_all, entries, visited_entries);
									break;
							}
							break;
						case "yes":
							entries.add(entry);
							break;
					}
					break;
				case "local":
					entries.add(entry);
					_findEntriesToInclude(entry.properties.node, save_all, entries, visited_entries);
					break;
			}
			break;
		case "local":
			_findEntriesToInclude(entry.properties.node, save_all, entries, visited_entries);
			break;
	}
	return entries;
}

function createMutationContextForNode(node: api_cs.Cnode, mutation: UpdateMutationBuilder): UpdateMutationBuilder {
	const position = node.input.position();
	switch (position.variant.name) {
		case "root":
			return mutation;
		case "group": {
			const group = position.variant.definition;
			const name = group.parent.parent.key_value;
			return createMutationContextForNode(group.component_root, mutation).group(name);
		}
		case "state": {
			const state = position.variant.definition;
			const state_name = state.key_value;
			const attribute_name = state.parent.parent.parent.key_value;
			return createMutationContextForNode(state.component_root, mutation).update_state(attribute_name, state_name);
		}
		case "entity": {
			const entity = position.variant.definition;
			switch (entity.variant.name) {
				case "root":
					return mutation;
				case "entry": {
					const entry = entity.variant.definition;
					const attribute_name = entry.parent.parent.parent.key_value;
					const key = Entry.getKeyBackendValue(entry) ?? "";
					return createMutationContextForNode(entry.component_root, mutation).updateCollectionEntry(
						attribute_name,
						key
					);
				}
			}
			break;
		}
	}
}
